import { z } from 'zod';
import { RoleSchema } from './common';
import { ProfessionalTypeSchema } from './matching';
import { LocaleSchema } from './specialization';

/**
 * Auth contracts — validated identically on both sides.
 * Passwords carry strong minimums; anonymous clients may register without one.
 */

const passwordRule = z
  .string()
  .min(10, 'Password must be at least 10 characters')
  .max(200)
  .regex(/[A-Z]/, 'Password must contain an uppercase letter')
  .regex(/[a-z]/, 'Password must contain a lowercase letter')
  .regex(/[0-9]/, 'Password must contain a digit');

/** POST /api/v1/auth/register-client */
export const RegisterClientSchema = z.object({
  email: z.string().email().max(200),
  pseudonym: z.string().min(3).max(40).regex(/^[a-zA-Z0-9_\-.]+$/, 'Alphanumerics, _, -, . only'),
  password: passwordRule,
  /** Optional at registration; can also be added later from Settings. */
  emergencyContact: z
    .object({
      name: z.string().min(1).max(120),
      phone: z.string().min(4).max(30),
      address: z.string().max(240).optional(),
      relation: z.string().max(60).optional(),
    })
    .optional(),
});
export type RegisterClientInput = z.infer<typeof RegisterClientSchema>;

/** POST /api/v1/auth/register-therapist */
export const RegisterTherapistSchema = z.object({
  email: z.string().email().max(200),
  password: passwordRule,
  displayName: z.string().min(2).max(120),
  licenseNumber: z.string().min(1).max(60),
  licenseCountry: z.string().length(2).default('GR'),
  specialties: z.array(z.string().min(1).max(60)).min(1).max(10),
  /**
   * Self-declared at registration and confirmed by the admin during vetting.
   * Asking here — rather than defaulting silently and hoping the admin
   * notices — means a counsellor has to actively misrepresent themselves to
   * end up in the psychologist list, instead of landing there by omission.
   */
  professionalType: ProfessionalTypeSchema.default('PSYCHOLOGIST'),
});
export type RegisterTherapistInput = z.infer<typeof RegisterTherapistSchema>;

/** POST /api/v1/auth/login */
export const LoginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1).max(200),
});
export type LoginInput = z.infer<typeof LoginSchema>;

/** Token pair issued on register/login. */
export const AuthTokensSchema = z.object({
  accessToken: z.string(),
  tokenType: z.literal('Bearer'),
  expiresIn: z.number().int().positive(), // seconds
});
export type AuthTokens = z.infer<typeof AuthTokensSchema>;

/** GET /api/v1/auth/me — the identity payload trusted apps rely on. */
export const MeResponseSchema = z.object({
  id: z.string().cuid(),
  role: RoleSchema,
  email: z.string().email().nullable(),
  pseudonym: z.string().nullable(),
  displayName: z.string().nullable(),
  isAnonymous: z.boolean(),
  /** UI language for site chrome + emails (ADR-015) — defaults `el`, changed via PATCH /api/v1/account/locale. */
  preferredLocale: LocaleSchema,
  therapist: z
    .object({
      status: z.enum(['PENDING_REVIEW', 'APPROVED', 'SUSPENDED', 'REJECTED']),
      licenseNumber: z.string().nullable(),
      hasSignedAgreement: z.boolean(),
    })
    .nullable(),
  hasAcceptedTos: z.boolean(),
});
export type MeResponse = z.infer<typeof MeResponseSchema>;

/** Combined register response: user + tokens. */
export const RegisterResponseSchema = z.object({
  user: MeResponseSchema,
  tokens: AuthTokensSchema,
});
export type RegisterResponse = z.infer<typeof RegisterResponseSchema>;

/**
 * POST /api/v1/auth/login (ADR-014). Only ADMIN accounts with 2FA enabled
 * ever see the `twoFactorRequired: true` branch — every other role, and an
 * ADMIN who hasn't enrolled yet, gets `twoFactorRequired: false` with full
 * tokens directly, same as before this ADR. `twoFactorToken` is a
 * short-lived (5 min), single-purpose credential — it is NOT a bearer
 * token and cannot be used to call any authenticated endpoint; it is only
 * ever exchanged via POST /api/v1/auth/2fa/verify.
 */
export const LoginChallengeResponseSchema = z.object({
  twoFactorRequired: z.literal(true),
  twoFactorToken: z.string(),
});
export type LoginChallengeResponse = z.infer<typeof LoginChallengeResponseSchema>;

export const LoginSuccessResponseSchema = z.object({
  twoFactorRequired: z.literal(false),
  user: MeResponseSchema,
  tokens: AuthTokensSchema,
});
export type LoginSuccessResponse = z.infer<typeof LoginSuccessResponseSchema>;

export const LoginResponseSchema = z.discriminatedUnion('twoFactorRequired', [
  LoginChallengeResponseSchema,
  LoginSuccessResponseSchema,
]);
export type LoginResponse = z.infer<typeof LoginResponseSchema>;

/** POST /api/v1/auth/2fa/verify — code is either a 6-digit TOTP or a recovery code ("XXXXX-XXXXX"). */
export const VerifyTwoFactorRequestSchema = z.object({
  twoFactorToken: z.string(),
  code: z.string().min(6).max(11),
});
export type VerifyTwoFactorRequest = z.infer<typeof VerifyTwoFactorRequestSchema>;

export const TwoFactorVerifyResponseSchema = RegisterResponseSchema;
export type TwoFactorVerifyResponse = z.infer<typeof TwoFactorVerifyResponseSchema>;
